import { IConfigSeller } from '@/models/configSeller'
import { useSelector } from 'react-redux'
import { useBatteryModelSupport } from './useBatteryModelSupport'
import { useTradeinContext } from './useTradeinContext'
import { canEvaluateBatteryWithSwitch as canEvaluateBatteryWithSwitchUtil } from '@/utils/canEvaluateBatteryWithSwitch'
import { isConsole } from '@/utils/isConsole'

// Mismas banderas que computa TradeInPageContext, extraídas para compartirlas
// con cualquier otra vista que necesite saber "qué le toca a este tradein" (ej. el modal
// de progreso de la tabla del Home) sin duplicar la lógica de negocio por seller/país.
export const useTradeinFlags = () => {
  const { ctx } = useTradeinContext()
  const configSeller = useSelector((store: { config: IConfigSeller }) => store.config)
  const isSamsungPeru = configSeller.config.s3.FOLDER_APP === 'ti_samsung_pe'
  const isSamsungRomPeru = configSeller.config.s3.FOLDER_APP === 'ti_samsung_pe_rom'
  const isFalabellaChile = configSeller.config.s3.FOLDER_APP === 'ti_falabella'
  const isMacstoreMexico = configSeller.config.s3.FOLDER_APP === 'ti_macstore'
  const isSamsungMexico = configSeller.config.s3.FOLDER_APP === 'ti_samsung_mx'
  const isHuaweiMexico = configSeller.config.s3.FOLDER_APP === 'ti_huawei_mx'
  const isTelcelMexico = configSeller.config.s3.FOLDER_APP === 'ti_telcel_mx'
  const isTelcelCorpMexico = configSeller.config.s3.FOLDER_APP === 'ti_telcel_corp_mx'
  const isHuaweiPeru = configSeller.config.s3.FOLDER_APP === 'ti_huawei_pe'
  const isHuaweiChile = configSeller.config.s3.FOLDER_APP === 'ti_huawei_cl'
  const isHuaweiOnlineChile = configSeller.config.s3.FOLDER_APP === 'ti_huawei_online_cl'
  const isHuaweiOnlineMexico = configSeller.config.s3.FOLDER_APP === 'ti_huawei_online_mx'
  const isHuaweiOnlinePeru = configSeller.config.s3.FOLDER_APP === 'ti_huawei_online_pe'
  const isRipleyChile = configSeller.config.s3.FOLDER_APP === 'ti_ripley_cl'
  const isParisChile = configSeller.config.s3.FOLDER_APP === 'ti_paris'
  const isXiaomiMexico = configSeller.config.s3.FOLDER_APP === 'ti_xiaomi'
  const isOppoMexico = configSeller.config.s3.FOLDER_APP === 'ti_oppo'
  const isElektraMexico = configSeller.config.s3.FOLDER_APP === 'ti_elektra_mx'
  const isCoppelMexico = configSeller.config.s3.FOLDER_APP === 'ti_coppel'
  const hasIAEvaluation = configSeller.config.permisos.ENABLE_IAEVALUATION === 1 && configSeller.config.permisos.SBUS_IA_EVALUATION.includes(ctx.tradein.id_sbu)
  const hasPhonecheck = ctx.tradein.id_sbu === 1 || (ctx.tradein.id_sbu === 3 && ctx.tradein.brand === 'Apple')
  const isConsola = isConsole(ctx.tradein.sbu)
  const canEvaluateBatteryWithSwitch = canEvaluateBatteryWithSwitchUtil(configSeller, ctx.tradein.id_sbu, ctx.tradein.brand)

  const { isBatteryModelSupported, loadingBatteryModels } = useBatteryModelSupport(
    ctx.tradein.brand,
    ctx.tradein.model !== '' ? ctx.tradein.model : ctx.tradein.modelName
  )
  /* El modelo está en el catálogo de batería y por lo tanto lleva paso de batería:
     true = lo lleva, false = no lo lleva,
     null = el catálogo de modelos con batería aún no responde, todavía no se sabe.
     Samsung México queda fuera aunque el modelo esté en el catálogo: después de la
     evaluación IA pasan directo a firmar el contrato. */
  const hasBatteryModelStep = canEvaluateBatteryWithSwitch || isSamsungMexico
    ? false
    : loadingBatteryModels ? null : isBatteryModelSupported
  /* Macstore México evalúa esos modelos con Discounts (el flujo con % de salud) y no con el
     switch, así que ahí el paso de batería existe igual pero lo resuelve el otro componente. */
  const useDiscountsByBatteryModel = isMacstoreMexico && hasBatteryModelStep === true
  /* true = se muestra el switch de batería, false = no se muestra, null = todavía no se sabe */
  // Temporal: el switch de batería ya no se muestra, se sigue solo con Discounts
  // useDiscountsByBatteryModel ? false : hasBatteryModelStep
  const showSwitchBattery = false
  const isNovabench = (isMacstoreMexico || isFalabellaChile || isRipleyChile) && ctx.tradein.sbu === 'Notebook'

  return {
    isSamsungPeru,
    isSamsungRomPeru,
    isFalabellaChile,
    isMacstoreMexico,
    isSamsungMexico,
    isHuaweiMexico,
    isTelcelMexico,
    isTelcelCorpMexico,
    isHuaweiPeru,
    isHuaweiChile,
    isHuaweiOnlineChile,
    isHuaweiOnlineMexico,
    isHuaweiOnlinePeru,
    isRipleyChile,
    isParisChile,
    isXiaomiMexico,
    isOppoMexico,
    isElektraMexico,
    isCoppelMexico,
    hasIAEvaluation,
    hasPhonecheck,
    isConsola,
    canEvaluateBatteryWithSwitch,
    hasBatteryModelStep,
    useDiscountsByBatteryModel,
    showSwitchBattery,
    isNovabench
  }
}
